import client from '@/lib/axios-client'
import type {
	SpheraBranchConfig,
	SpheraCrConfig,
	SpheraExportParams,
	SpheraOptionsOutput,
	SpheraOptionType,
	SpheraPreviewOutput,
	SpheraSettings,
	SpheraSettingsOutput,
} from '@/types'

const BASE_URL = '/sphera-export'

function toQuery({ month, types, branchId }: SpheraExportParams) {
	return { month, types: types.join(','), ...(branchId ? { branchId } : {}) }
}

export async function getSpheraOptions(): Promise<SpheraOptionsOutput> {
	const response = await client.get(`${BASE_URL}/options`)
	return response.data.data
}

export async function getSpheraPreview(
	params: SpheraExportParams,
): Promise<SpheraPreviewOutput> {
	const response = await client.get(`${BASE_URL}/preview`, {
		params: toQuery(params),
	})
	return response.data.data
}

export async function downloadSpheraExport({
	part,
	...params
}: SpheraExportParams & { part?: number }): Promise<Blob> {
	const response = await client.get(`${BASE_URL}/download`, {
		params: { ...toQuery(params), ...(part ? { part } : {}) },
		responseType: 'blob',
		timeout: 120_000,
	})
	return response.data
}

export async function updateCrSpheraService(
	id: string,
	spheraService: string | null,
): Promise<void> {
	await client.patch(`${BASE_URL}/crs/${id}`, { spheraService })
}

export async function updateBranchSpheraLocation(
	id: string,
	spheraLocation: string | null,
): Promise<void> {
	await client.patch(`${BASE_URL}/branches/${id}`, { spheraLocation })
}

export async function createSpheraOption(
	type: SpheraOptionType,
	name: string,
): Promise<void> {
	await client.post(`${BASE_URL}/options`, { type, name })
}

export async function renameSpheraOption(
	id: string,
	name: string,
): Promise<void> {
	await client.patch(`${BASE_URL}/options/${id}`, { name })
}

export async function deleteSpheraOption(id: string): Promise<void> {
	await client.delete(`${BASE_URL}/options/${id}`)
}

export async function getSpheraBranchConfigs(): Promise<SpheraBranchConfig[]> {
	const response = await client.get(`${BASE_URL}/branches`)
	return response.data.data
}

export async function getSpheraCrConfigs(): Promise<SpheraCrConfig[]> {
	const response = await client.get(`${BASE_URL}/crs`)
	return response.data.data
}

export async function getSpheraSettings(): Promise<SpheraSettingsOutput> {
	const response = await client.get(`${BASE_URL}/settings`)
	return response.data.data
}

export async function updateSpheraSettings(
	settings: SpheraSettings,
): Promise<void> {
	await client.put(`${BASE_URL}/settings`, settings)
}
